// hero-world — animated brand globe (rotating node network) drawn on a canvas.
//
// A wireframe sphere of points slowly rotates; nearby points are joined by a
// static mesh so it reads as a "global network" (LigoPay = pagos en tiempo real
// en toda la región). Brand palette only — deep purple base, violet/bright mesh
// and nodes — so it contrasts on the dark hero. Self-contained, no dependencies.
//
// Performance / a11y:
//  - transform-free: redraws onto a DPR-scaled canvas with requestAnimationFrame
//  - pauses when scrolled out of view (IntersectionObserver)
//  - prefers-reduced-motion → one static frame, no loop

const POINTS = 132 // sphere node count
const NEIGHBORS = 3 // edges per node (mesh density)
const TILT = -0.42 // globe axial tilt (radians)
const SPEED = 0.0016 // rotation per frame

// Brand channels (match styles/sass/basics/_dark-mode.scss).
const C_NODE = [199, 180, 245] // light violet
const C_HUB = [233, 224, 255] // near-white violet (bright hubs)
const C_LINE = [133, 69, 234] // purple-highlight #8545EA

function fibonacciSphere(n) {
  const pts = []
  const golden = Math.PI * (3 - Math.sqrt(5))
  for (let i = 0; i < n; i++) {
    const y = 1 - (i / (n - 1)) * 2 // 1 → -1
    const r = Math.sqrt(1 - y * y)
    const theta = golden * i
    pts.push([Math.cos(theta) * r, y, Math.sin(theta) * r])
  }
  return pts
}

// Static edge list: connect each node to its K nearest by angular distance.
function buildEdges(pts) {
  const edges = new Set()
  for (let i = 0; i < pts.length; i++) {
    const dists = []
    for (let j = 0; j < pts.length; j++) {
      if (i === j) continue
      const d =
        pts[i][0] * pts[j][0] + pts[i][1] * pts[j][1] + pts[i][2] * pts[j][2]
      dists.push([j, d]) // larger dot = closer on the sphere
    }
    dists.sort((a, b) => b[1] - a[1])
    for (let k = 0; k < NEIGHBORS; k++) {
      const j = dists[k][0]
      edges.add(i < j ? `${i}-${j}` : `${j}-${i}`)
    }
  }
  return Array.from(edges).map((e) => e.split('-').map(Number))
}

function initHeroWorld(canvas) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const pts = fibonacciSphere(POINTS)
  const edges = buildEdges(pts)
  // Per-node twinkle phase so nodes don't pulse in unison.
  const phase = pts.map((_, i) => (i * 1.6180339) % (Math.PI * 2))

  let w = 0
  let h = 0
  let R = 0
  let cx = 0
  let cy = 0
  let yaw = 0
  let raf = 0
  let running = false

  function resize() {
    const rect = canvas.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    w = rect.width
    h = rect.height
    canvas.width = Math.round(w * dpr)
    canvas.height = Math.round(h * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    cx = w / 2
    cy = h / 2
    R = Math.min(w, h) * (parseFloat(canvas.dataset.globeScale) || 0.42)
  }

  function project() {
    const cosT = Math.cos(TILT)
    const sinT = Math.sin(TILT)
    const cosY = Math.cos(yaw)
    const sinY = Math.sin(yaw)
    const out = new Array(pts.length)
    for (let i = 0; i < pts.length; i++) {
      let [x, y, z] = pts[i]
      // rotate around Y (yaw)
      const x1 = x * cosY + z * sinY
      const z1 = -x * sinY + z * cosY
      // tilt around X
      const y1 = y * cosT - z1 * sinT
      const z2 = y * sinT + z1 * cosT
      out[i] = { x: cx + x1 * R, y: cy - y1 * R, z: z2 } // z>0 = front
    }
    return out
  }

  function draw() {
    ctx.clearRect(0, 0, w, h)
    const p = project()

    // Edges — only when the nearer endpoint faces us; fade with depth.
    ctx.lineWidth = 1
    for (let e = 0; e < edges.length; e++) {
      const a = p[edges[e][0]]
      const b = p[edges[e][1]]
      const zAvg = (a.z + b.z) / 2
      if (zAvg < -0.15) continue
      const alpha = Math.max(0, Math.min(0.5, (zAvg + 0.4) * 0.5))
      ctx.strokeStyle = `rgba(${C_LINE[0]},${C_LINE[1]},${C_LINE[2]},${alpha})`
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
    }

    // Nodes — back-to-front so front nodes glow over the rest.
    const order = p.map((q, i) => i).sort((i, j) => p[i].z - p[j].z)
    const t = yaw * 12
    for (let oi = 0; oi < order.length; oi++) {
      const i = order[oi]
      const q = p[i]
      const front = (q.z + 1) / 2 // 0..1
      const isHub = i % 9 === 0
      const twinkle = 0.7 + 0.3 * Math.sin(t + phase[i])
      const baseR = isHub ? 2.6 : 1.7
      const r = baseR * (0.55 + front * 0.6)
      const alpha = (0.25 + front * 0.7) * (isHub ? 1 : twinkle)
      const col = isHub ? C_HUB : C_NODE
      if (front > 0.55) {
        ctx.shadowColor = `rgba(${C_LINE[0]},${C_LINE[1]},${C_LINE[2]},0.9)`
        ctx.shadowBlur = isHub ? 14 : 8
      } else {
        ctx.shadowBlur = 0
      }
      ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${alpha})`
      ctx.beginPath()
      ctx.arc(q.x, q.y, r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.shadowBlur = 0
  }

  function frame() {
    yaw += SPEED
    draw()
    if (running) raf = window.requestAnimationFrame(frame)
  }

  function start() {
    if (running || reduce) return
    running = true
    raf = window.requestAnimationFrame(frame)
  }

  function stop() {
    running = false
    if (raf) window.cancelAnimationFrame(raf)
  }

  resize()
  draw()

  let resizeTimer = 0
  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(() => {
        resize()
        draw()
      }, 150)
    },
    { passive: true }
  )

  if (reduce) return // static frame only

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => (en.isIntersecting ? start() : stop())),
      { threshold: 0.05 }
    )
    io.observe(canvas)
  } else {
    start()
  }
}

function initHeroWorlds() {
  document.querySelectorAll('[data-hero-world]').forEach(initHeroWorld)
}

if (document.readyState !== 'loading') initHeroWorlds()
else document.addEventListener('DOMContentLoaded', initHeroWorlds)
